import React, { useEffect, useState } from 'react';
import { platformAPI } from '../api';

/**
 * PlatformFooter — single source of truth for every Maestra-controlled footer.
 *
 * Reads the admin-editable Platform Texts (`ptx`) and renders the canonical
 * two-line footer plus the optional admin link. Used by:
 *   public:    Landing, Login, Forgot/Reset password, Request access, Invite accept
 *   app:       Dashboard, Settings, Extend access, Legal layout, Template preview
 *   client:    expired board, archived board, workspace-unavailable view
 *
 * Workspace-branded active client views deliberately do NOT use this
 * component — they have their own custom_footer_* fields plus a subtle
 * "Created with Maestra" attribution. See ClientView active-board branch.
 *
 * The ptx fetch is cached on `window` so a page with multiple footers (or
 * client-side route changes) doesn't refetch the same tiny JSON every time.
 *
 * Variants:
 *   "default" — full two lines + optional link, used on most pages.
 *   "compact" — single-line eyebrow with the brand label only. Used by the
 *               very minimal auth pages where the previous markup was a
 *               single "MAESTRA 2026" line (Forgot/Reset password, Extend
 *               access). Keeps the visual footprint identical to before.
 */
let _cachedPtx = null;
let _cachedAt = 0;
const PTX_TTL_MS = 30_000;

const loadPtx = async () => {
  const now = Date.now();
  if (_cachedPtx && (now - _cachedAt) < PTX_TTL_MS) return _cachedPtx;
  try {
    const { data } = await platformAPI.getTexts();
    _cachedPtx = data || {};
    _cachedAt = now;
  } catch (e) {
    _cachedPtx = _cachedPtx || {};
  }
  return _cachedPtx;
};

const PlatformFooter = ({
  variant = 'default',
  align = 'center',
  className = '',
  style = {},
}) => {
  const [ptx, setPtx] = useState(_cachedPtx);

  useEffect(() => {
    let mounted = true;
    loadPtx().then((data) => { if (mounted) setPtx(data); });
    return () => { mounted = false; };
  }, []);

  const footerText = ptx?.footer_text || 'MAESTRA 2026';
  const footerSubtext = ptx?.footer_subtext || 'One project. One space. One clear direction.';
  const footerLink = ptx?.footer_link || '';
  const footerLinkLabel = ptx?.footer_link_label || '';

  if (variant === 'compact') {
    return (
      <p
        data-testid="platform-footer-compact"
        className={className}
        style={{
          fontFamily: 'Playfair Display, serif',
          fontSize: 11,
          letterSpacing: '0.15em',
          color: 'var(--kahla-text-light)',
          textAlign: align,
          margin: 0,
          ...style,
        }}
      >
        {footerText}
      </p>
    );
  }

  return (
    <div
      data-testid="platform-footer"
      className={className}
      style={{ textAlign: align, ...style }}
    >
      <p
        data-testid="platform-footer-text"
        style={{
          fontFamily: 'Playfair Display, serif',
          fontSize: 11,
          letterSpacing: '0.15em',
          color: 'var(--kahla-text-light)',
          margin: '0 0 4px',
        }}
      >
        {footerText}
      </p>
      <p
        data-testid="platform-footer-subtext"
        style={{
          fontSize: 10,
          letterSpacing: '0.1em',
          color: 'var(--kahla-text-light)',
          opacity: 0.7,
          margin: 0,
        }}
      >
        {footerSubtext}
      </p>
      {footerLink && footerLinkLabel && (
        <a
          data-testid="platform-footer-link"
          href={footerLink}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            fontSize: 10,
            color: 'var(--kahla-text-light)',
            marginTop: 6,
            display: 'inline-block',
            textDecoration: 'underline',
            opacity: 0.8,
          }}
        >
          {footerLinkLabel}
        </a>
      )}
    </div>
  );
};

export default PlatformFooter;
